/**
 * 63.1f (the owner, on the 6×6's .50: "as gunner your aim doesn't really work … the camera shot is mostly blocked by the
 * vehicle itself"; lane F): the gunner's view from a vehicle's hatch. The camera pivots at the gunner in its hatch — the
 * sim stands its feet there (`seatPos`, core/drive.ts), its eye over the turret — not at the hull's middle, where the
 * unit's own spot is; and the camera never sits inside the hull: within the hull's footprint it is lifted over its top.
 * The range the crosshair converges at looks past the gunner's own hull.
 *
 * The hull is its measured shape's boxes (constants/hitboxes.ts, people; x the hull's left, y its up, z its front), in
 * the vehicle's frame as drawn (core/vehicleFrame.ts). Presentation only: pure functions of what they are given.
 */
import type { Vec3 } from '../types';
import type { VehicleFrame } from '../core/vehicleFrame';
import { BODIES, type BodyKey } from '../constants/body';

/** How far clear of the hull the camera stays, and the aim's ray looks past it, in people. */
export const HULL_CLEAR = 0.25;

type Box = { centre: readonly [number, number, number]; half: readonly [number, number, number] };

/** A body's hull as boxes (its capsules by their bounding boxes), in people. */
function hullBoxes(type: string): Box[] {
  const parts = BODIES[type as BodyKey]?.hitbox.parts ?? [];
  return parts.map(p => {
    if (p.shape === 'box') return { centre: p.centre, half: p.half };
    const c: [number, number, number] = [(p.a[0] + p.b[0]) / 2, (p.a[1] + p.b[1]) / 2, (p.a[2] + p.b[2]) / 2];
    const h: [number, number, number] = [Math.abs(p.a[0] - p.b[0]) / 2 + p.radius, Math.abs(p.a[1] - p.b[1]) / 2 + p.radius, Math.abs(p.a[2] - p.b[2]) / 2 + p.radius];
    return { centre: c, half: h };
  });
}

/** `p` in the vehicle's frame, in people: [left, up, front]. */
function local(p: Vec3, f: VehicleFrame, person: number): [number, number, number] {
  const x = p.x - f.foot.x, y = p.y - f.foot.y, z = p.z - f.foot.z;
  return [(x * f.left.x + y * f.left.y + z * f.left.z) / person, (x * f.up.x + y * f.up.y + z * f.up.z) / person, (x * f.front.x + y * f.front.y + z * f.front.z) / person];
}

/** Is `p` within `clear` people of the hull's boxes? */
export function inHull(p: Vec3, type: string, f: VehicleFrame, person: number, clear = HULL_CLEAR): boolean {
  const [x, y, z] = local(p, f, person);
  return hullBoxes(type).some(b => Math.abs(x - b.centre[0]) < b.half[0] + clear && Math.abs(y - b.centre[1]) < b.half[1] + clear && Math.abs(z - b.centre[2]) < b.half[2] + clear);
}

/**
 * `p` lifted along the hull's up until it stands `clear` people over every box whose footprint (grown by `clear`) it is
 * over and under the top of; elsewhere `p` as it is.
 */
export function liftOverHull(p: Vec3, type: string, f: VehicleFrame, person: number, clear = HULL_CLEAR): Vec3 {
  const [x, y, z] = local(p, f, person);
  let lift = 0;
  for (const b of hullBoxes(type)) {
    if (Math.abs(x - b.centre[0]) >= b.half[0] + clear || Math.abs(z - b.centre[2]) >= b.half[2] + clear) continue;
    const top = b.centre[1] + b.half[1] + clear;
    if (y < top && y > b.centre[1] - b.half[1] - clear) lift = Math.max(lift, top - y);
  }
  if (lift === 0) return p;
  const d = lift * person;
  return { x: p.x + f.up.x * d, y: p.y + f.up.y * d, z: p.z + f.up.z * d };
}
